import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchDataById,
  fetchDatas,
  updateEntity,
} from '../../../services/axiosInstanceService';
import { companyParser, companyStatsParser } from '../companyParser';

/**
 * Async thunk to fetch a company entity by its IRI.
 *
 * This function retrieves a company entity from the API based on the provided `companyIdIri`,
 * parses the response, and returns the formatted company data.
 *
 * @param {Object} params - The parameters required to fetch a company.
 * @param {string | null} params.companyIdIri - The unique IRI identifier of the company.
 * @returns {Promise<ICompany>} The fetched and parsed company entity.
 * @throws {Error} Throws an error if `companyIdIri` is missing.
 */
const fetchCompanyThunk = createAsyncThunk(
  'company/fetchCompanyThunk',
  async (params: { companyIdIri: string | null }) => {
    try {
      // Validate input parameters
      if (!params.companyIdIri) throw new Error('Company idIri not found');

      // Fetch the company entity from the API
      const result = await fetchDataById(params.companyIdIri);

      // Parse and return the fetched company data
      return companyParser(result);
    } catch (error) {
      // Reject the promise if an error occurs
      return Promise.reject(error);
    }
  },
);

/**
 * Async thunk to update a company entity.
 *
 * This function takes a `companyIdIri` and a `body` containing partial company data,
 * then updates the entity and returns the parsed result.
 *
 * @param {Object} params - The parameters required to update a company.
 * @param {string} params.companyIdIri - The unique IRI identifier of the company.
 * @param {Partial<Omit<ICompany, 'idIri' | 'id'>>} params.body - The company data to update (excluding `idIri` and `id`).
 * @returns {Promise<ICompany>} The updated company entity after parsing.
 * @throws {Error} Throws an error if `companyIdIri` or `body` is missing.
 */
const updateCompanyThunk = createAsyncThunk(
  'company/updateCompanyThunk',
  async (params: {
    companyIdIri: string;
    body: Partial<Omit<ICompany, 'idIri' | 'id'>>;
  }) => {
    try {
      // Validate input parameters
      if (!params.companyIdIri) throw new Error('No company idIri found');
      if (!params.body) throw new Error('No body found');

      // Ensure `postalCode` is mapped correctly to `zipcode`
      const body = { ...params.body, zipcode: params.body.postalCode };
      delete body.postalCode;

      // Call API to update the company entity
      const result = await updateEntity({ idIri: params.companyIdIri, body });

      // Parse and return the updated company data
      return companyParser(result);
    } catch (error) {
      // Reject the promise if an error occurs
      return Promise.reject(error);
    }
  },
);

/**
 * Fetches company statistics based on the given parameters.
 *
 * @param {Object} params - The parameters required to fetch company stats.
 * @param {string | null} params.companyIdIri - The IRI of the company to fetch stats for.
 * @param {string} params.date - The reference date for fetching statistics.
 * @param {boolean} params.onlyYear - If true, fetches stats for the entire year instead of a specific month.
 * @param {Users | null} params.users - The list of users related to the company.
 * @returns {Promise<ICompanyStat[]>} A promise resolving to an array of parsed company statistics.
 * @throws {Error} If any required parameter is missing.
 */
const fetchCompanyStatsThunk = createAsyncThunk(
  'company/fetchCompanyStatsThunk',
  async (params: {
    companyIdIri: string | null;
    date: string;
    onlyYear: boolean;
    users: Users | null;
  }) => {
    try {
      // Validate required parameters
      if (!params.companyIdIri) throw new Error('No company idIri found');
      if (!params.date) throw new Error('No date found');
      if (!params.users) throw new Error('No users found');

      // Extract year and month from the provided date
      const year = new Date(params.date).getFullYear();
      const month = new Date(params.date).getMonth() + 1;

      // Fetch statistics from the API
      const result = await fetchDatas(`${params.companyIdIri}/stats`, {
        year,
        month: params.onlyYear ? undefined : month, // Exclude month if onlyYear is true
      });

      // Parse the API response into structured company statistics
      const parsed = companyStatsParser(result['hydra:member'], params.users);

      return parsed;
    } catch (error) {
      // Reject the promise with the caught error
      return Promise.reject(error);
    }
  },
);

export { fetchCompanyStatsThunk, fetchCompanyThunk, updateCompanyThunk };
